import {useRef, useState} from 'react'
import {Eye, History, RefreshCw, X} from 'lucide-react'
import {recordServiceLevelActivity, SyncRun} from '../api'
import {fmtDataHoraFoto} from '../utils/format'
import {formatSyncDuration} from '../utils/syncProgress'
import useDialog from './useDialog'

const STATUS: Record<string, string> = {
  OK: 'Concluída',
  RUNNING: 'Em andamento',
  ERRO: 'Erro',
  ERROR: 'Erro',
  FAILED: 'Erro',
}

function Status({run}: {run: SyncRun}) {
  return (
    <span
      className={`badge ${
        run.status === 'OK' ? 'FINALIZADO' : run.status === 'RUNNING' ? 'ENVIADO' : 'p3'
      }`}
    >
      {STATUS[run.status] ?? run.status}
    </span>
  )
}

function duration(run: SyncRun) {
  if (!run.fim_em) return run.status === 'RUNNING' ? 'Em andamento' : '—'
  const elapsed = Date.parse(run.fim_em) - Date.parse(run.iniciado_em)
  return Number.isFinite(elapsed) && elapsed >= 0 ? formatSyncDuration(elapsed) : '—'
}

function timestamp(value: string | null | undefined) {
  if (!value || !Number.isFinite(Date.parse(value))) return '—'
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(value))
}

export default function SyncHistory({
  runs,
  error,
  onRefresh,
}: {
  runs: SyncRun[] | null
  error: boolean
  onRefresh: () => void
}) {
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const panel = useRef<HTMLDivElement>(null)
  const history = runs?.slice(0, 10)
  const selected = history?.find((run) => run.id === selectedId)
  const close = () => setSelectedId(null)
  useDialog(!!selected, panel, close)

  return (
    <>
      <section className='card sync-history' aria-labelledby='sync-history-title'>
        <div className='sync-history-heading'>
          <div>
            <h2 id='sync-history-title' className='flex items-center gap-2'>
              <History size={18} /> Histórico de sincronizações
            </h2>
            <p className='chart-note'>
              As últimas 10 execuções, da mais recente para a mais antiga. Horários de Brasília.
            </p>
          </div>
          <button
            type='button'
            className='btn secondary'
            onClick={onRefresh}
            aria-label='Atualizar histórico'
            data-analytics-feature='Nível de Serviço · Atualizar histórico de sincronizações'
          >
            <RefreshCw size={16} /> Atualizar
          </button>
        </div>
        {!history ? (
          <p className='empty'>
            {error
              ? 'Histórico indisponível. Tente consultar novamente.'
              : 'Consultando histórico…'}
          </p>
        ) : !history.length ? (
          <p className='empty'>Nenhuma sincronização registrada.</p>
        ) : (
          <div className='sync-history-table'>
            <table aria-label='Últimas 10 sincronizações'>
              <thead>
                <tr>
                  <th scope='col'>Execução</th>
                  <th scope='col'>Status</th>
                  <th scope='col'>Início</th>
                  <th scope='col'>Fim</th>
                  <th scope='col'>Duração</th>
                  <th scope='col'>Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {history.map((run) => (
                  <tr key={run.id}>
                    <td data-label='Execução'>
                      <strong>#{run.id}</strong>
                    </td>
                    <td data-label='Status'>
                      <Status run={run} />
                    </td>
                    <td data-label='Início'>{fmtDataHoraFoto(run.iniciado_em)}</td>
                    <td data-label='Fim'>{run.fim_em ? fmtDataHoraFoto(run.fim_em) : '—'}</td>
                    <td data-label='Duração'>{duration(run)}</td>
                    <td data-label='Detalhes'>
                      <button
                        type='button'
                        className='btn secondary'
                        aria-label={`Ver detalhes da sincronização ${run.id}`}
                        data-analytics-feature='Nível de Serviço · Ver detalhes da sincronização'
                        onClick={() => {
                          setSelectedId(run.id)
                          void recordServiceLevelActivity('sync_details_view', {run_id: run.id})
                        }}
                      >
                        <Eye size={16} /> Ver detalhes
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {selected && (
        <div className='modal-overlay' onClick={close}>
          <div
            ref={panel}
            className='modal sync-run-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='sync-run-title'
            tabIndex={-1}
            onClick={(event) => event.stopPropagation()}
          >
            <div className='modal-heading'>
              <h2 id='sync-run-title'>Sincronização #{selected.id}</h2>
              <button
                type='button'
                className='icon-button'
                aria-label='Fechar detalhes da sincronização'
                onClick={close}
              >
                <X size={20} />
              </button>
            </div>
            <dl className='sync-run-info'>
              <div>
                <dt>Status</dt>
                <dd>
                  <Status run={selected} />
                </dd>
              </div>
              <div>
                <dt>Duração</dt>
                <dd>{duration(selected)}</dd>
              </div>
              <div>
                <dt>Início (Brasília)</dt>
                <dd>{timestamp(selected.iniciado_em)}</dd>
              </div>
              <div>
                <dt>Fim (Brasília)</dt>
                <dd>{timestamp(selected.fim_em)}</dd>
              </div>
              <div>
                <dt>Dados atualizados em</dt>
                <dd>{timestamp(selected.ts_foto)}</dd>
              </div>
              <div>
                <dt>Tempo estimado</dt>
                <dd>{selected.estimativa_ms ? formatSyncDuration(selected.estimativa_ms) : '—'}</dd>
              </div>
            </dl>
            <h3>Detalhes da execução</h3>
            <pre className='sync-run-log'>
              {selected.detalhe ||
                (selected.status === 'RUNNING'
                  ? 'Sincronização em andamento. Os detalhes estarão disponíveis ao finalizar.'
                  : 'Nenhum detalhe registrado para esta execução.')}
            </pre>
            <div className='modal-actions'>
              <button type='button' className='btn secondary' onClick={close}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
